import {
  AccountAddress,
  CommittedTransactionResponse,
  InputEntryFunctionData,
} from "@aptos-labs/ts-sdk";
import {
  getPrimarySubaccountAddr,
  getVaultShareAddress,
  USDC_DECIMALS,
  UserPerformanceOnVault,
  UserPerformancesOnVaults,
  Vault,
  VaultsResponse,
  VaultType,
} from "@decibeltrade/sdk";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { readDexSdk, sdkConfig } from "@/contexts/dex-sdk";
import { getTokenBalance, tokenBalanceQueryKey } from "@/contexts/queries/token-balance";
import { usdcBalanceQueryKey } from "@/contexts/queries/usdc-balance";
import { WithSubaccount } from "@/contexts/queries/vault/types";
import { useUserOwnedVaultsQuery } from "@/contexts/queries/vault/user-owned-vaults";
import { userPerformancesOnVaultsQueryKey } from "@/contexts/queries/vault/user-performance";
import { vaultQueryKey } from "@/contexts/queries/vault/vaults";
import { useWriteDexSdk } from "@/contexts/write-dex-sdk-context";
import { useDecibelWallet } from "@/hooks/use-decibel-wallet";
import { addressesEqual } from "@/utils/address";
import { amountToChainUnits, chainUnitsToAmount } from "@/utils/math";
import { getUnlockTimestampMs, mergeOrAppendDeposit } from "@/utils/vault";

import { useWalletFeePayerWithFallback } from "../wallet-fee-payer";

/** Detect EMAX_OUTSTANDING_SHARES_EXCEEDED (Move abort code 0xf / 15) from vault deposit errors */
export function isMaxOutstandingSharesExceededError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error);
  return (
    message.includes("EMAX_OUTSTANDING_SHARES_EXCEEDED") ||
    (message.includes("vault") && /\b0xf\b/i.test(message))
  );
}

export const useDepositIntoVault = () => {
  const queryClient = useQueryClient();
  const { accountAddress } = useDecibelWallet();
  const { writeDexSdk } = useWriteDexSdk();
  const { mutateAsync: signAndSubmitWalletTransaction } = useWalletFeePayerWithFallback();
  const { data: userVaultsData } = useUserOwnedVaultsQuery(accountAddress);

  return useMutation({
    mutationFn: async ({
      vaultAddress,
      amount,
      subaccountAddr,
      vaultType,
      lockdownPeriodS,
    }: {
      vaultAddress: string;
      amount: number;
      subaccountAddr?: string;
      vaultType?: VaultType;
      lockdownPeriodS?: number | null;
    }) => {
      if (!writeDexSdk) {
        throw new Error("WriteDexSDK not initialized");
      }

      const pkg = writeDexSdk.config.deployment.package;
      const usdc = writeDexSdk.config.deployment.usdc;

      // If the target vault is owned by the current user, route through manager's
      // primary subaccount so the contribution counts as vault manager equity on-chain
      const isOwnedVault = userVaultsData?.items.some((v) =>
        addressesEqual(v.vault_address, vaultAddress),
      );

      const managerSubaccountAddr =
        isOwnedVault && accountAddress
          ? getPrimarySubaccountAddr(
              AccountAddress.from(accountAddress),
              sdkConfig.compatVersion,
              sdkConfig.deployment.package,
            )
          : undefined;

      // Determine which address will hold the vault shares after deposit
      const addressToUse = managerSubaccountAddr ?? subaccountAddr ?? accountAddress;

      if (!addressToUse) {
        throw new Error("Address to use not found");
      }

      let tx: CommittedTransactionResponse;

      if (managerSubaccountAddr) {
        // Manager deposit: Deposit from primary subaccount into the vault
        // This ensures the deposit counts as vault manager equity on-chain
        if (!accountAddress) {
          throw new Error("Account address not found");
        }

        //  Contribute from primary subaccount to the vault
        const contributeData: InputEntryFunctionData = {
          function: `${pkg}::dex_accounts_entry::contribute_to_vault`,
          typeArguments: [],
          functionArguments: [
            managerSubaccountAddr,
            vaultAddress,
            usdc,
            amountToChainUnits(amount),
          ],
        };
        const contributeTx = await signAndSubmitWalletTransaction({ data: contributeData });
        tx = await writeDexSdk.aptos.waitForTransaction({
          transactionHash: contributeTx.hash,
        });
      } else if (subaccountAddr) {
        // Regular subaccount deposit via session key
        tx = await writeDexSdk.depositToVault({
          vaultAddress,
          amount: amountToChainUnits(amount),
          subaccountAddr,
        });
      } else {
        // Direct wallet contribution
        if (!accountAddress) {
          throw new Error("Account address not found");
        }
        const data: InputEntryFunctionData = {
          function: `${pkg}::vault_api::contribute_funds`,
          typeArguments: [],
          functionArguments: [vaultAddress, amountToChainUnits(amount)],
        };
        const pendingTransaction = await signAndSubmitWalletTransaction({ data });
        tx = await writeDexSdk.aptos.waitForTransaction({
          transactionHash: pendingTransaction.hash,
        });
      }

      if (accountAddress) {
        void queryClient.invalidateQueries({
          queryKey: usdcBalanceQueryKey({ accountAddr: accountAddress }),
        });
      }

      const vaultShareTokenAddress = getVaultShareAddress(vaultAddress);

      void queryClient.invalidateQueries({
        queryKey: tokenBalanceQueryKey({
          accountAddr: addressToUse,
          tokenAddr: vaultShareTokenAddress,
          tokenDecimals: USDC_DECIMALS,
        }),
      });

      const sharePrice = await readDexSdk.vaults.getVaultSharePrice({ vaultAddress });

      const newTokenBalance = await getTokenBalance({
        accountAddr: addressToUse,
        tokenAddr: vaultShareTokenAddress,
        tokenDecimals: USDC_DECIMALS,
      });

      const newTotalValueOfShares = sharePrice * newTokenBalance;

      // Get vault to determine vault type if not provided
      const vault = queryClient.getQueryData<Vault | undefined>(
        vaultQueryKey({ address: vaultAddress }),
      );

      const unlockTimestampMs = getUnlockTimestampMs(lockdownPeriodS);

      const newDeposit = {
        amount_usdc: amount,
        shares_received: amountToChainUnits(newTokenBalance, USDC_DECIMALS),
        timestamp_ms: Date.now(),
        unlock_timestamp_ms: unlockTimestampMs,
      };

      void queryClient.setQueryData(
        userPerformancesOnVaultsQueryKey({
          ownerAddr: addressToUse,
        }),
        (old: UserPerformancesOnVaults | undefined) => {
          if (old?.some((item) => addressesEqual(item.vault.address, vaultAddress))) {
            return old.map((item) => {
              if (addressesEqual(item.vault.address, vaultAddress)) {
                const previousShares = chainUnitsToAmount(
                  item.current_num_shares ?? 0,
                  USDC_DECIMALS,
                );
                const sharesReceivedForThisDeposit = newTokenBalance - previousShares;

                return {
                  ...item,
                  share_price: sharePrice,
                  total_deposited: (item.total_deposited ?? 0) + amount,
                  current_num_shares: amountToChainUnits(newTokenBalance, USDC_DECIMALS),
                  current_value_of_shares: newTotalValueOfShares,
                  volume: (item.volume ?? 0) + amount,
                  locked_amount: unlockTimestampMs
                    ? (item.locked_amount ?? 0) + amount
                    : item.locked_amount,

                  deposits: mergeOrAppendDeposit(
                    item.deposits,
                    {
                      amount_usdc: amount,
                      shares_received: amountToChainUnits(
                        sharesReceivedForThisDeposit,
                        USDC_DECIMALS,
                      ),
                      timestamp_ms: Date.now(),
                      unlock_timestamp_ms: unlockTimestampMs,
                    },
                    lockdownPeriodS,
                  ),
                };
              }
              return item;
            });
          } else {
            // This should always be present in the query data
            const vaultForNewEntry =
              queryClient.getQueryData<Vault | undefined>(
                vaultQueryKey({ address: vaultAddress }),
              ) ?? vault;

            return [
              ...(old ?? []),
              {
                vault: vaultForNewEntry ?? { address: vaultAddress },
                account_address: addressToUse,
                share_price: sharePrice,
                total_deposited: amount,
                volume: amount,
                current_num_shares: amountToChainUnits(newTokenBalance, USDC_DECIMALS),
                current_value_of_shares: newTotalValueOfShares,
                locked_amount: unlockTimestampMs ? amount : null,
                deposits: [newDeposit],
              } as UserPerformanceOnVault,
            ];
          }
        },
      );

      void queryClient.setQueryData(
        vaultQueryKey({ address: vaultAddress }),
        (old: WithSubaccount<Vault> | undefined) => {
          return old
            ? {
                ...old,
                tvl: (old.tvl ?? 0) + amount,
                perp_equity: (old.perp_equity ?? 0) + amount,
              }
            : undefined;
        },
      );

      if (vaultType)
        void queryClient.setQueryData(
          vaultQueryKey({ vaultType }),
          (old: VaultsResponse | undefined) => {
            if (!old) return undefined;

            return {
              ...old,
              items: old.items.map((item) => {
                if (addressesEqual(item.address, vaultAddress)) {
                  return {
                    ...item,
                    tvl: (item.tvl ?? 0) + amount,
                    perp_equity: (item.perp_equity ?? 0) + amount,
                  };
                }
                return item;
              }),
            };
          },
        );

      return tx;
    },
  });
};
